// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useState, useEffect, useLayoutEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client/react';
import classNames from 'classnames';
import { Layout, Columns, Column } from '@plesk/ui-library';
import { useLocalStorage, useMediaQuery } from 'common/hooks';
import CookiePolicy from '../CookiePolicy';
import JswComponent from '../../jsw/JswComponent';
import StatusMessages from '../StatusMessages';
import LicenseStatus from './LicenseStatus';
import PageHeader from './PageHeader';
import PageSidebar from './PageSidebar';
import PageFooter from './PageFooter';
import PageContentHeader from './PageContentHeader';
import { unescapeHtml } from 'jsw';
import stripTags from 'helpers/stripTags';
import getTitleForHelpSearchQuery from './getTitleForHelpSearchQuery';

import BRANDING_QUERY from 'queries/Branding.graphql';
import { CopilotContextProvider, CopilotContext } from './CopilotContext';
import Chat from './PageHeader/Copilot/Chat';
import StickerCollectionGame from './StickerCollectionGame';

const PageLayout = ({
    baseUrl = undefined,
    showFrames = true,
    returnUrl = undefined,
    pageHeader = undefined,
    pageSidebar = undefined,
    pageContentHeader = undefined,
    pageTitle = undefined,
    withSecondary = false,
    secondary = undefined,
    width = undefined,
    bodyClass = undefined,
    integrationClass = undefined,
    children = undefined,
    isSecondaryCollapsed = false,
}) => {
    const { data: { viewer: { customTitle } } } = useQuery(BRANDING_QUERY);
    const [isClosedOnDesktop, setClosedOnDesktop] = useLocalStorage('isSidebarClosed');
    const [isClosedInResponsive, setClosedInResponsive] = useState(true);
    const sidebarCollapsed = [isClosedInResponsive && 'responsive', (isClosedOnDesktop === 'true') && 'desktop'].filter(Boolean);
    const isResponsive = useMediaQuery('(max-width: 1022px)');
    const isClosed = sidebarCollapsed.includes(isResponsive ? 'responsive' : 'desktop');

    const { isRightSidebarOpen, setIsRightSidebarOpen } = useContext(CopilotContext);
    pageTitle ??= pageContentHeader?.pageTitle ?? pageContentHeader?.breadcrumbs?.[pageContentHeader.breadcrumbs.length - 1]?.title;

    useLayoutEffect(() => {
        const documentTitleEl = document.querySelector('title');
        if (!documentTitleEl) {
            return;
        }
        documentTitleEl.textContent = [pageTitle && unescapeHtml(stripTags(pageTitle)), customTitle].filter(Boolean).join(' - ');
    }, [pageTitle, customTitle]);

    useLayoutEffect(() => {
        const classes = classNames(bodyClass, integrationClass).split(' ').filter(Boolean);
        classes.forEach(className => document.body.classList.add(className));
        return () => {
            classes.forEach(className => document.body.classList.remove(className));
        };
    }, [bodyClass, integrationClass]);

    if (baseUrl) {
        global.Jsw.baseUrl = baseUrl;
    }

    const handleToggleSidebar = (collapsed, mode) => {
        switch (mode) {
            case 'responsive':
                setClosedInResponsive(collapsed);
                break;
            case 'desktop':
                setClosedOnDesktop(collapsed ? 'true' : 'false');
                break;
        }
    };

    const [stickyTop, setStickyTop] = useState(0);

    useEffect(() => {
        const top = parseInt(getComputedStyle(document.querySelector('.pul-layout__main-inner')).paddingTop)
            + (document.getElementById('classic-mode-navigation')?.getBoundingClientRect().height || 0);
        setStickyTop(top);
    }, []);

    if (pageHeader?.helpMenu && pageContentHeader) {
        pageHeader.helpMenu = {
            ...pageHeader.helpMenu,
            searchQuery: getTitleForHelpSearchQuery(pageContentHeader),
        };
    }

    useEffect(() => {
        if (!pageHeader?.copilotChat) {
            setIsRightSidebarOpen(false);
        }
    }, [pageHeader?.copilotChat, setIsRightSidebarOpen]);

    return (
        <Layout
            rightSidebar={pageHeader?.copilotChat && <Chat />}
            rightSidebarVisible={isRightSidebarOpen}
            id="page"
            width={width}
            className={isRightSidebarOpen ? 'pul-layout__right-sidebar-opened' : 'pul-layout__right-sidebar-closed'}
            header={showFrames && pageHeader ? (
                <div >
                    <PageHeader
                        returnUrl={returnUrl}
                        {...pageHeader}
                    />
                </div>
            ) : undefined}
            headerAddon={showFrames && pageHeader ? (
                <div >
                    <CookiePolicy />
                    {pageHeader.licenseStatus ? <LicenseStatus {...pageHeader.licenseStatus} /> : null}
                </div>
            ) : undefined}
            onSidebarToggle={handleToggleSidebar}
            sidebar={showFrames && pageSidebar ? (
                <PageSidebar
                    title={customTitle}
                    breadcrumbs={pageContentHeader?.breadcrumbs}
                    isClosed={isClosed}
                    onNodeClick={isResponsive ? () => setClosedInResponsive(true) : undefined}
                    {...pageSidebar}
                />
            ) : undefined}
            sidebarCollapsed={sidebarCollapsed}
            sidebarType={showFrames && pageSidebar ? 'folded' : undefined}
            footer={showFrames ? (
                <PageFooter />
            ) : undefined}
            contentHeader={pageContentHeader ? (
                <PageContentHeader {...pageContentHeader} />
            ) : null}
        >
            <div className="b-content">
                <Columns gap="md" vertical={740} data-type="page-content" className="b-content-wrap">
                    <Column fill id="content-body" className="b-content-main">
                        <div id="main" style={withSecondary || secondary ? { position: 'sticky', top: `${stickyTop}px` } : undefined}>
                            <StatusMessages />
                            {children}
                        </div>
                    </Column>
                    {withSecondary || secondary ? (
                        <Column
                            width={256}
                            className={classNames('b-content-side', isSecondaryCollapsed && 'b-content-side--collapsed')}
                        >
                            <div style={{ position: 'sticky', top: `${stickyTop}px` }}>
                                {typeof secondary === 'string' ? (
                                    <JswComponent>{secondary}</JswComponent>
                                ) : secondary}
                            </div>
                        </Column>
                    ) : null}
                </Columns>
            </div>
            <StickerCollectionGame />
        </Layout>
    );
};

PageLayout.propTypes = {
    baseUrl: PropTypes.string,
    showFrames: PropTypes.bool,
    returnUrl: PropTypes.string,
    pageHeader: PropTypes.shape(PageHeader.propTypes),
    pageSidebar: PropTypes.object,
    pageContentHeader: PropTypes.shape(PageContentHeader.propTypes),
    withSecondary: PropTypes.bool,
    secondary: PropTypes.any,
    isSecondaryCollapsed: PropTypes.bool,
    width: PropTypes.number,
    bodyClass: PropTypes.string,
    integrationClass: PropTypes.string,
    pageTitle: PropTypes.string,
    children: PropTypes.any,
};

const PageLayoutWithCopilotContext = props => (
    <CopilotContextProvider>
        <PageLayout {...props} />
    </CopilotContextProvider>
);

PageLayoutWithCopilotContext.propTypes = PageLayout.propTypes;

export default PageLayoutWithCopilotContext;
